import { z } from "zod";
import { FirmMarketTypeZ, FirmZ } from "../domain/firm";
import { PlatformTypeZ, AdapterTypeZ } from "../domain/platform";

export const CreateFirmReqZ = z.object({
  id: z.string().min(1).max(32),
  name: z.string().min(1).max(128),
  platformType: PlatformTypeZ.optional(),
  adapter: AdapterTypeZ.optional(),
  officialUrl: z.string().url().nullable().optional(),
  pfmSlug: z.string().nullable().optional(),
  marketType: FirmMarketTypeZ.optional(),
});
export type CreateFirmReq = z.infer<typeof CreateFirmReqZ>;

export const IngestRulebookReqZ = z.object({
  rawMarkdown: z.string().min(32),
  source: z.enum(["manual", "propfirmmatch", "official"]).default("manual"),
  rulesVersion: z.string().optional(),
});
export type IngestRulebookReq = z.infer<typeof IngestRulebookReqZ>;

// Firm row as returned by GET /api/firms — carries the per-firm
// platform support list from `firm_platforms` (schema added in 0016).
// Field name is snake_case to match the rest of the GET payload
// (platform_type, created_at, etc.).
export const FirmListItemZ = FirmZ.extend({
  supported_platforms: z.array(PlatformTypeZ).default([]),
});
export type FirmListItem = z.infer<typeof FirmListItemZ>;

export const ListFirmsResZ = z.object({
  firms: z.array(FirmListItemZ),
});
export type ListFirmsRes = z.infer<typeof ListFirmsResZ>;

export const CheapestPriceQueryZ = z.object({
  accountSize: z.number().positive(),
  turkeyOnly: z.boolean().optional(),
  within24h: z.boolean().default(true),
  limit: z.number().int().min(1).max(50).default(10),
});
export type CheapestPriceQuery = z.infer<typeof CheapestPriceQueryZ>;

/**
 * One row from the /api/firms/:slug/challenges response. Mirrors the
 * enriched firm_challenges table (mig 0043) so the Add Account wizard
 * can carry the picked variant through its state + pass the ref through
 * to /api/accounts without re-fetching. Everything rule-related is
 * nullable — PFM-seeded rows only have the TEXT marketing strings +
 * pricing; rows touched by the Haiku synthesizer also carry the numeric
 * *_pct + rich rule fields.
 */
export const FirmChallengeRowZ = z.object({
  id: z.number().int(),
  program_name: z.string().nullable(),
  account_size: z.number().int().positive(),
  steps: z.number().int().min(0).max(5),
  challenge_type: z.string().nullable(),

  // Legacy TEXT display strings — still used by the landing challenges
  // table; kept for backwards compat with variant card rendering.
  profit_target: z.string().nullable(),
  daily_loss: z.string().nullable(),
  max_loss: z.string().nullable(),
  profit_split: z.string().nullable(),
  payout_frequency: z.string().nullable(),

  // Numeric parallels + rich rules (mig 0043).
  profit_target_pct: z.number().nullable(),
  daily_loss_pct: z.number().nullable(),
  max_loss_pct: z.number().nullable(),
  profit_split_pct: z.number().nullable(),
  daily_dd_type: z.string().nullable(),
  max_dd_type: z.string().nullable(),
  leverage: z.string().nullable(),
  min_trading_days: z.number().int().nullable(),
  weekend_holding_allowed: z.number().int().nullable(),
  news_trading_allowed: z.number().int().nullable(),
  consistency_rule_pct: z.number().nullable(),

  // Pricing / provenance.
  base_price: z.number().int(),
  effective_price: z.number().int(),
  discount_pct: z.number().int(),
  rules_source: z.string().nullable(),
  rules_synthesized_at: z.number().int().nullable(),
  rules_confidence: z.number().nullable(),
});
export type FirmChallengeRow = z.infer<typeof FirmChallengeRowZ>;

export const FirmChallengesResZ = z.object({
  challenges: z.array(FirmChallengeRowZ),
  firmRules: z.string().nullable(),
  firmRulesDetail: z.string().nullable(),
});
export type FirmChallengesRes = z.infer<typeof FirmChallengesResZ>;

/** POST /api/firms/:slug/synthesize-challenges response. */
export const SynthesizeChallengesResZ = z.object({
  jobId: z.string(),
  status: z.enum(["queued", "running", "completed", "failed"]),
});
export type SynthesizeChallengesRes = z.infer<typeof SynthesizeChallengesResZ>;

/** GET /api/firms/:slug/synthesize-challenges/:jobId response. */
export const SynthesisJobZ = z.object({
  job_id: z.string(),
  firm_id: z.string(),
  status: z.enum(["queued", "running", "completed", "failed"]),
  trigger: z.string(),
  requested_at: z.number().int(),
  started_at: z.number().int().nullable(),
  completed_at: z.number().int().nullable(),
  error: z.string().nullable(),
  variants_written: z.number().int(),
  chunks_scanned: z.number().int(),
});
export type SynthesisJob = z.infer<typeof SynthesisJobZ>;
